import { useState, useRef } from "react";
import { BRAND, BRAND_LIGHT, SHADOW_POP, MUTED, TEXT, LINE, WHITE, ARCH_COLORS } from "./constants";

// `size` era una constante interna: la prop que llegaba de fuera se ignoraba.
// Y el radio estaba fijo en 100 sobre un lienzo de 380, así que el polígono
// ocupaba poco más de la mitad del espacio disponible. Ahora ambos escalan.
const RadarChart = ({ candidateA, candidateB, labels, tooltips, colorA = BRAND, size = 380, compact = false, onHoverAxis }) => {
  // `compact`: versión reducida para tarjetas. Muestra solo la inicial de cada
  // eje —a ese tamaño el texto completo no cabe— y delega el tooltip al padre,
  // que decide dónde pintarlo.
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const cx = size / 2;
  const cy = size / 2;
  // El radio se calcula desde el lienzo, dejando sitio para las etiquetas.
  const r = size * 0.30;
  const labelGap = size * 0.12;
  const levels = 5;
  const axes = labels.length;
  const angleStep = (2 * Math.PI) / axes;
  const getAngle = (i) => -Math.PI / 2 + i * angleStep;

  const gridPoints = (level) =>
    labels.map((_, i) => {
      const a = getAngle(i);
      const rr = (r * level) / levels;
      return `${cx + rr * Math.cos(a)},${cy + rr * Math.sin(a)}`;
    }).join(" ");

  const dataPoints = (data, max = 5) =>
    data.map((v, i) => {
      const a = getAngle(i);
      const rr = (r * v) / max;
      return `${cx + rr * Math.cos(a)},${cy + rr * Math.sin(a)}`;
    }).join(" ");

  const valuesA = [candidateA.scoring.technical, candidateA.scoring.soft, candidateA.scoring.cultural, candidateA.scoring.growth];
  const valuesB = candidateB ? [candidateB.scoring.technical, candidateB.scoring.soft, candidateB.scoring.cultural, candidateB.scoring.growth] : null;

  const renderLabel = (label, lx, ly, i) => {
    if (compact) {
      const isOn = activeTooltip === i;
      return (
        <g key={i} style={{ cursor: "help" }}
          onMouseEnter={() => { setActiveTooltip(i); if (onHoverAxis) onHoverAxis(i); }}
          onMouseLeave={() => { setActiveTooltip(null); if (onHoverAxis) onHoverAxis(null); }}>
          <circle cx={lx} cy={ly} r={11} fill={isOn ? BRAND_LIGHT : "transparent"} />
          <text x={lx} y={ly} textAnchor="middle" dominantBaseline="central" fontSize="11" fontWeight="700"
            fill={isOn ? BRAND : MUTED} fontFamily="DM Sans, sans-serif">{label.charAt(0).toUpperCase()}</text>
        </g>
      );
    }
    const isActive = activeTooltip === i;
    const words = label.split(" ");
    const mid = Math.ceil(words.length / 2);
    const line1 = words.slice(0, mid).join(" ");
    const line2 = words.slice(mid).join(" ");
    const hitW = 84;
    const hitH = 34;

    const handleMouseEnter = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const svgScale = size / rect.width;
      setTooltipPos({ x: lx / svgScale, y: ly / svgScale });
      setActiveTooltip(i);
    };

    return (
      <g key={i}>
        <g style={{ cursor: "pointer" }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setActiveTooltip(null)}>
          <rect x={lx - hitW/2} y={ly - hitH/2} width={hitW} height={hitH} rx={6}
            fill={isActive ? BRAND_LIGHT : "transparent"}
            stroke={isActive ? BRAND + "55" : "transparent"} strokeWidth="1" />
          {words.length === 1
            ? <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontWeight="700" fill={isActive ? BRAND : TEXT} fontFamily="DM Sans, sans-serif">{label}</text>
            : <text x={lx} textAnchor="middle" fontSize="11" fontWeight="700" fill={isActive ? BRAND : TEXT} fontFamily="DM Sans, sans-serif">
                <tspan x={lx} y={ly - 7}>{line1}</tspan>
                <tspan x={lx} y={ly + 7}>{line2}</tspan>
              </text>
          }
        </g>
      </g>
    );
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        onClick={() => setActiveTooltip(null)}>
        {[1,2,3,4,5].map(level => (
          <polygon key={level} points={gridPoints(level)} fill="none" stroke={LINE} strokeWidth="1" />
        ))}
        {labels.map((_, i) => {
          const a = getAngle(i);
          return <line key={i} x1={cx} y1={cy} x2={cx + r * Math.cos(a)} y2={cy + r * Math.sin(a)} stroke={LINE} strokeWidth="1" />;
        })}
        {valuesB && (
          <>
            <polygon points={dataPoints(valuesB)} fill={`${ARCH_COLORS[2]}26`} stroke={ARCH_COLORS[2]} strokeWidth="2" strokeLinejoin="round" />
            {valuesB.map((v, i) => {
              const a = getAngle(i);
              const rr = (r * v) / 5;
              return <circle key={i} cx={cx + rr * Math.cos(a)} cy={cy + rr * Math.sin(a)} r={4} fill={ARCH_COLORS[2]} />;
            })}
          </>
        )}
        <polygon points={dataPoints(valuesA)} fill={colorA + "26"} stroke={colorA} strokeWidth="2.5" strokeLinejoin="round" />
        {valuesA.map((v, i) => {
          const a = getAngle(i);
          const rr = (r * v) / 5;
          return <circle key={i} cx={cx + rr * Math.cos(a)} cy={cy + rr * Math.sin(a)} r={4} fill={colorA} />;
        })}
        {labels.map((label, i) => {
          const a = getAngle(i);
          const lx = cx + (r + labelGap) * Math.cos(a);
          const ly = cy + (r + labelGap) * Math.sin(a);
          return renderLabel(label, lx, ly, i);
        })}
      </svg>
      {/* HTML tooltip overlay — outside SVG to avoid flicker */}
      {!compact && activeTooltip !== null && tooltips && tooltips[activeTooltip] && (
        <div
          onMouseEnter={() => setActiveTooltip(activeTooltip)}
          onMouseLeave={() => setActiveTooltip(null)}
          style={{
            position: "absolute",
            left: Math.min(tooltipPos.x - 110, size - 240),
            top: tooltipPos.y - 100,
            width: 240,
            background: WHITE,
            border: `1.5px solid ${BRAND}44`,
            borderRadius: 12,
            padding: "12px 16px",
            boxShadow: SHADOW_POP,
            zIndex: 200,
            pointerEvents: "auto",
          }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: BRAND, marginBottom: 6 }}>{labels[activeTooltip]}</div>
          <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.65 }}>{tooltips[activeTooltip]}</div>
        </div>
      )}
    </div>
  );
};

export default RadarChart;
